/**
 * Pantalla de **gestión de sedes** (`/admin/sedes`). Reemplaza el placeholder de
 * `ROUTES.sedes`. Equivale a `sedes.html` del SSR.
 *
 * Solo-admin (permiso `all`, mismo que la API y el menú del SSR) vía
 * `RequireAdmin`. Orquesta:
 *  - el catálogo de sedes con estadísticas (`useSedes`) en `SedesTable`;
 *  - alta/edición (`SedeFormDialog`) y borrado con confirmación
 *    (`ConfirmDeleteDialog`).
 *
 * Presentacional + hooks; sin `fetch`. Estados de carga/error contemplados. El
 * catálogo de sedes es global (no acotado por la sede activa), igual que el SSR.
 */
import { useState } from "react";

import { Button, Card, CardContent } from "@/components/ui";

import { adminErrorMessage } from "../format";
import { useSedes } from "../hooks/useAdminData";
import { useDeleteSede } from "../hooks/useSedeMutations";
import type { SedeListItem } from "../types";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { RequireAdmin } from "./RequireAdmin";
import { SedeFormDialog } from "./SedeFormDialog";
import { SedesTable } from "./SedesTable";

/** Objetivo del formulario de sede (alta o edición). */
type FormTarget = { mode: "create" } | { mode: "edit"; sede: SedeListItem } | null;

export function SedesScreen() {
  const sedesQuery = useSedes();
  const deleteSede = useDeleteSede();

  const [formTarget, setFormTarget] = useState<FormTarget>(null);
  const [deleteTarget, setDeleteTarget] = useState<SedeListItem | null>(null);

  /** Abre el diálogo de borrado limpiando un posible error de un intento previo. */
  function openDelete(sede: SedeListItem) {
    deleteSede.reset();
    setDeleteTarget(sede);
  }

  function handleConfirmDelete() {
    if (deleteTarget === null) {
      return;
    }
    deleteSede.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <RequireAdmin
      title="Sedes"
      deniedMessage="No tienes permiso para gestionar sedes."
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">Sedes</h1>
          <Button
            type="button"
            variant="primary"
            onClick={() => setFormTarget({ mode: "create" })}
          >
            Nueva sede
          </Button>
        </div>

        {sedesQuery.isPending ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-slate-500" role="status">
                Cargando sedes…
              </p>
            </CardContent>
          </Card>
        ) : sedesQuery.isError ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-red-600" role="alert">
                {adminErrorMessage(sedesQuery.error)}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <SedesTable
                sedes={sedesQuery.data.items}
                onEdit={(sede) => setFormTarget({ mode: "edit", sede })}
                onDelete={openDelete}
              />
            </CardContent>
          </Card>
        )}
      </div>

      <SedeFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        title="Eliminar sede"
        description={
          deleteTarget
            ? `¿Eliminar la sede "${deleteTarget.nombre}"? Esta acción no se puede deshacer.`
            : ""
        }
        errorMessage={deleteSede.isError ? adminErrorMessage(deleteSede.error) : null}
        isPending={deleteSede.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </RequireAdmin>
  );
}
